import type { WebContainer } from '@webcontainer/api';

/**
 * Deadlines for WebContainer calls that have no timeout of their own. A frozen WebContainer
 * never settles `spawn`, `fs.*` or `mount`, so without these the per-turn build and the preview
 * recovery wait on it forever ("Reconnecting the preview…" that never ends).
 */

/** `wc.spawn` normally returns within a second or two. */
export const WORKBENCH_SPAWN_DEADLINE_MS = 20_000;
/** One small file-system call. */
export const WORKBENCH_FS_DEADLINE_MS = 15_000;
/** The health check: a single directory read. */
export const WORKBENCH_PROBE_DEADLINE_MS = 5_000;

/** A WebContainer call did not settle in time: the environment is at fault, not the code. */
export class WorkbenchUnresponsiveError extends Error {
  constructor(label: string, timeoutMs: number) {
    super(
      `${label} did not answer within ${Math.round(timeoutMs / 1000)}s; the in-browser workspace stopped responding.`
    );
    this.name = 'WorkbenchUnresponsiveError';
  }
}

/** Settles like `work`, or rejects with `WorkbenchUnresponsiveError` after `timeoutMs`. */
export function withDeadline<T>(work: Promise<T>, timeoutMs: number, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new WorkbenchUnresponsiveError(label, timeoutMs)), timeoutMs);
  });
  return Promise.race([work, deadline]).finally(() => {
    if (timer !== undefined) clearTimeout(timer);
  });
}

/**
 * True when the WebContainer answers a directory read in time. A read that fails still counts
 * as an answer; only silence means the workspace is frozen.
 */
export async function isWebContainerResponsive(
  wc: Pick<WebContainer, 'fs'>,
  timeoutMs: number = WORKBENCH_PROBE_DEADLINE_MS
): Promise<boolean> {
  try {
    await withDeadline(wc.fs.readdir('/'), timeoutMs, 'The workspace health check');
    return true;
  } catch (err) {
    return !(err instanceof WorkbenchUnresponsiveError);
  }
}
